import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listBarrios,
  listCiudades,
  listComarcas,
  listProvincias,
} from "@/lib/anuncios.functions";
import { listOperaciones, listUsosLocal } from "@/lib/datos.functions";
import type { PortalLookups } from "@/lib/portal-url";

type Row = { id: string; nombre: string; slug?: string | null };

/**
 * Tablas para traducir las URLs del portal (slugs) a filtros (ids) y al
 * revés. Comparten caché con el formulario de filtros.
 */
export function usePortalLookups(): { lookups: PortalLookups; lookupsReady: boolean } {
  const fnProvincias = useServerFn(listProvincias);
  const fnComarcas = useServerFn(listComarcas);
  const fnCiudades = useServerFn(listCiudades);
  const fnBarrios = useServerFn(listBarrios);

  const ops = useQuery({
    queryKey: ["operaciones"],
    queryFn: () => listOperaciones() as Promise<Row[]>,
  });
  const usos = useQuery({
    queryKey: ["usos_local_all"],
    queryFn: () => listUsosLocal({ data: {} }) as Promise<Row[]>,
  });
  const provs = useQuery({
    queryKey: ["provincias"],
    queryFn: () => fnProvincias() as Promise<Row[]>,
  });
  const comarcas = useQuery({
    queryKey: ["comarcas_all"],
    queryFn: () => fnComarcas() as Promise<Row[]>,
  });
  const ciudades = useQuery({
    queryKey: ["ciudades_all"],
    queryFn: () => fnCiudades({ data: {} }) as Promise<Row[]>,
  });
  const barrios = useQuery({
    queryKey: ["barrios_all"],
    queryFn: () => fnBarrios({ data: {} }) as Promise<Row[]>,
  });

  const lookups: PortalLookups = useMemo(
    () => ({
      operaciones: ops.data ?? [],
      usos: usos.data ?? [],
      provincias: provs.data ?? [],
      comarcas: comarcas.data ?? [],
      ciudades: ciudades.data ?? [],
      barrios: barrios.data ?? [],
    }),
    [ops.data, usos.data, provs.data, comarcas.data, ciudades.data, barrios.data],
  );

  const lookupsReady =
    !!ops.data &&
    !!usos.data &&
    !!provs.data &&
    !!comarcas.data &&
    !!ciudades.data &&
    !!barrios.data;

  return { lookups, lookupsReady };
}
